import { rect, type LotSide, type Rect, type Vec2 } from './layout';

/**
 * Player homes. Everything on a home lot (trailer, couch, grill, Camaro, yard decor)
 * is laid out once in "template" coordinates: Lot 7's real world coordinates. A
 * HomeFrame maps the template onto any home lot, mirroring it for lots on the east
 * side of their street. That's how every player in a multiplayer park gets a lot of
 * their own.
 */
export interface HomeFrame {
  /** Index into HOME_LOTS. */
  index: number;
  /** Lot number on the mailbox. */
  number: number;
  street: string;
  /** Street centerline x. */
  streetX: number;
  /** -1: west of the street (like Lot 7), +1: east of it (mirrored). */
  side: LotSide;
  /** The lot row's z (Lot 7's row is 14). */
  rowZ: number;
}

const TEMPLATE_ROW = 14;
export const POSSUM_TROT_X = -64;

export const HOME_LOTS: readonly HomeFrame[] = [
  { index: 0, number: 7, street: 'Main Street', streetX: 0, side: -1, rowZ: 14 },
  // Possum Trot Lane, east side first: those lots are closest to Main Street.
  { index: 1, number: 16, street: 'Possum Trot Lane', streetX: POSSUM_TROT_X, side: 1, rowZ: 30 },
  { index: 2, number: 18, street: 'Possum Trot Lane', streetX: POSSUM_TROT_X, side: 1, rowZ: 14 },
  { index: 3, number: 20, street: 'Possum Trot Lane', streetX: POSSUM_TROT_X, side: 1, rowZ: -2 },
  { index: 4, number: 22, street: 'Possum Trot Lane', streetX: POSSUM_TROT_X, side: 1, rowZ: -18 },
  { index: 5, number: 17, street: 'Possum Trot Lane', streetX: POSSUM_TROT_X, side: -1, rowZ: 30 },
  { index: 6, number: 19, street: 'Possum Trot Lane', streetX: POSSUM_TROT_X, side: -1, rowZ: 14 },
  { index: 7, number: 21, street: 'Possum Trot Lane', streetX: POSSUM_TROT_X, side: -1, rowZ: -2 },
  { index: 8, number: 23, street: 'Possum Trot Lane', streetX: POSSUM_TROT_X, side: -1, rowZ: -18 },
];

export const LOT_7 = HOME_LOTS[0] as HomeFrame;

/** Maps template coordinates (Lot 7's) onto a home lot. */
export function toFrame(f: HomeFrame, x: number, z: number): Vec2 {
  return { x: f.streetX - f.side * x, z: f.rowZ + (z - TEMPLATE_ROW) };
}

/**
 * Mirrors a rotation for lots on the other side of the street. `front` is the local
 * axis the object faces along: most props face +z, cars and mailboxes face +x.
 */
export function frameRot(f: HomeFrame, rot: number, front: 'z' | 'x' = 'z'): number {
  if (f.side === -1) return rot;
  return front === 'z' ? -rot : Math.PI - rot;
}

export interface DecorSlot {
  x: number;
  z: number;
  y?: number;
  rot?: number;
}

/** A single-wide, or the double wide from Big Dave's once you've bought it. */
export type HomeKind = 'single' | 'double';

/** Where each decor item lives on a home lot, in template (Lot 7) coordinates. */
export const DECOR_TEMPLATE: Record<string, DecorSlot> = {
  tires: { x: -11.4, z: 14.8 },
  clothesline: { x: -27.0, z: 18.0 },
  lll: { x: -14.1, z: 11.56, y: 2.05 },
  flamingos: { x: -8.8, z: 21.6 },
  goose: { x: -11.6, z: 12.45 },
  gnomes: { x: -12.2, z: 21.4 },
  zapper: { x: -13.6, z: 12.9, y: 2.55 },
  lights: { x: -16.5, z: 11.66, y: 3.02 },
  kiddiePool: { x: -14.3, z: 20.3 },
  flag: { x: -26.6, z: 12.6 },
  dish: { x: -19.6, z: 9.2, y: 3.2 },
  recliner: { x: -13.6, z: 15.4, rot: Math.PI / 2 },
  smoker: { x: -21.4, z: 13.0 },
  tub: { x: -24.7, z: 13.7 },
  gorilla: { x: -6.4, z: 21.2, rot: Math.PI / 2 },
  trampoline: { x: -18.3, z: 19.0 },
  pool: { x: -23.6, z: 18.9 },
  bigTv: { x: -4.8, z: 19.4, rot: Math.PI / 2 },
  possum: { x: -11.95, z: 13.35, rot: -Math.PI / 2 },
};

/**
 * The double wide is 16 x 7.2 m: same back wall as the single-wide, so it reaches about
 * two meters further into the yard. Porch things move up to make room.
 */
const DOUBLE_DECOR: Record<string, DecorSlot> = {
  ...DECOR_TEMPLATE,
  tires: { x: -11.7, z: 19.3 },
  lll: { x: -14.4, z: 13.46, y: 2.2 },
  goose: { x: -11.0, z: 15.3 },
  zapper: { x: -13.7, z: 14.9, y: 2.7 },
  lights: { x: -16.5, z: 13.47, y: 3.08 },
  dish: { x: -20.5, z: 9.8, y: 4.15 },
  recliner: { x: -13.9, z: 17.1, rot: Math.PI / 2 },
  smoker: { x: -21.6, z: 15.2 },
  tub: { x: -25.7, z: 15.8 },
  possum: { x: -14.1, z: 15.6, rot: -Math.PI / 2 },
};

/** The fixed furniture of a home lot, in template coordinates. */
const T_SINGLE = {
  trailer: { x: -16.5, z: 9.5 },
  door: { x: -12.5, z: 12.2 },
  trailerDoor: { x: -12.5, z: 11.5 },
  couch: { x: -15.5, z: 12.7 },
  grill: { x: -19.6, z: 13.1 },
  mailbox: { x: -4.7, z: 13.6 },
  camaro: { x: -8.2, z: 17 },
  spawn: { x: -12.6, z: 14.2 },
  chair: { x: -9.6, z: 20.6, rot: 2.2 },
  trash: { x: -22.8, z: 12.3 },
  /** Where the Lil' Rascal parks (and charges) at home: past the end of the trailer. */
  rascal: { x: -9.4, z: 13.4, rot: 0 },
  /** The driveway car wash: the sign by the mailbox, and where customers pull up (in the street). */
  washSign: { x: -4.5, z: 15.4, rot: Math.PI / 2 },
  washBay: { x: -1.6, z: 17.6, rot: -Math.PI / 2 },
  area: rect(-28, 6, -3.5, 22),
};

const T_DOUBLE: typeof T_SINGLE = {
  ...T_SINGLE,
  trailer: { x: -16.5, z: 9.8 },
  door: { x: -12.5, z: 16.1 },
  trailerDoor: { x: -12.5, z: 13.4 },
  couch: { x: -16.6, z: 14.9 },
  grill: { x: -19.9, z: 15.2 },
  spawn: { x: -12.6, z: 16.9 },
  trash: { x: -23.2, z: 14.5 },
  rascal: { x: -7.6, z: 14.5, rot: 0 },
};

/** Footprint of each kind of home, in meters (length along x, depth along z). */
export const HOME_SIZE: Record<HomeKind, { length: number; depth: number }> = {
  single: { length: 12, depth: 4 },
  double: { length: 16, depth: 7.2 },
};

export interface HomeSpots {
  frame: HomeFrame;
  kind: HomeKind;
  trailer: Vec2;
  /** Where you stand to go inside. */
  door: Vec2;
  /** The trailer's actual door (steps, porch light). */
  trailerDoor: Vec2;
  couch: Vec2;
  grill: Vec2;
  mailbox: Vec2;
  spawn: Vec2;
  chair: Vec2 & { rot: number };
  trash: Vec2;
  /** The Lil' Rascal's parking spot (facing for the player, who faces +z). */
  rascal: Vec2 & { rot: number };
  /** The car wash sign (faces the street), and where a customer's car parks (rotation.y, cars face +x). */
  washSign: Vec2 & { rot: number };
  washBay: Vec2 & { rot: number };
  /** Parked Camaro: CarController heading (forward = cos h, -sin h). */
  camaro: Vec2 & { heading: number };
  decor: Record<string, DecorSlot>;
  /** The whole lot, for "are you home?" checks. */
  area: Rect;
  /** Mirrors a facing angle for the player (who faces +z). */
  facing(rot: number): number;
}

export function homeSpots(f: HomeFrame, kind: HomeKind = 'single'): HomeSpots {
  const p = (v: Vec2): Vec2 => toFrame(f, v.x, v.z);
  const T = kind === 'double' ? T_DOUBLE : T_SINGLE;
  const decor: Record<string, DecorSlot> = {};
  for (const [id, s] of Object.entries(kind === 'double' ? DOUBLE_DECOR : DECOR_TEMPLATE)) {
    decor[id] = { ...s, ...p(s), ...(s.rot !== undefined ? { rot: frameRot(f, s.rot) } : {}) };
  }
  const a = p({ x: T.area.minX, z: T.area.minZ });
  const b = p({ x: T.area.maxX, z: T.area.maxZ });
  return {
    frame: f,
    kind,
    trailer: p(T.trailer),
    door: p(T.door),
    trailerDoor: p(T.trailerDoor),
    couch: p(T.couch),
    grill: p(T.grill),
    mailbox: p(T.mailbox),
    spawn: p(T.spawn),
    chair: { ...p(T.chair), rot: frameRot(f, T.chair.rot) },
    trash: p(T.trash),
    rascal: { ...p(T.rascal), rot: frameRot(f, T.rascal.rot) },
    washSign: { ...p(T.washSign), rot: frameRot(f, T.washSign.rot) },
    washBay: { ...p(T.washBay), rot: frameRot(f, T.washBay.rot, 'x') },
    // The Camaro faces the street: heading 0 on Lot 7, PI across the street.
    camaro: { ...p(T.camaro), heading: f.side === -1 ? 0 : Math.PI },
    decor,
    area: rect(Math.min(a.x, b.x), Math.min(a.z, b.z), Math.max(a.x, b.x), Math.max(a.z, b.z)),
    facing: (rot) => frameRot(f, rot),
  };
}

export function homeLot(index: number): HomeFrame {
  return HOME_LOTS[index] ?? LOT_7;
}

/** "Lot 18 on Possum Trot Lane" */
export const lotLabel = (f: HomeFrame): string => `Lot ${f.number} on ${f.street}`;
